import { BaseRoleplayer, ClientValidationStatus } from '../../common/types/client';
import { Box, Typography } from '@mui/material';
import { getFormattedDate } from '../../common/lib/date';
import { GridActionsCellItemProps, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { JSX, ReactElement, useMemo } from 'react';
import { Roleplayer } from '../../common/types/roleplayer';
import ClientStatusBadge from '../components/clientStatusBadge';
import DataGrid, { DataGridProps } from '../../common/components/dataGrid';
import DataGridDeleteAction from '../../common/components/dataGrid/dataGridDeleteAction';
import DataGridSelectAction from '../../common/components/dataGrid/dataGridSelectAction';

export interface PreloadedClientListProps<T extends { id: number } = Roleplayer>
  extends Omit<DataGridProps<T>, 'columns' | 'rows'> {
  /**
   * Optional array of selected clients ids
   */
  selectedIds?: number[];
  /**
   * Callback to be called when the delete button is clicked
   */
  onClickDelete?: (client: T) => void;
  /**
   * Callback to be called when the select button is clicked
   */
  onClickSelect?: (client: T) => void;
  /**
   * Flag indicating if only valid clients can be selected
   * @default true
   */
  allowSelectOnlyValid?: boolean;
  /**
   * Data (client list) to be displayed in the table
   */
  clients: T[];
  /**
   * Override the default columns
   */
  columns?: GridColDef<T>[];
  /**
   * List of client IDs that cannot be selected
   */
  forbiddenClientIds?: number[];
  /**
   * Function that returns the actions to be shown in the row
   */
  getActions?: (parameters: GridRowParams<T>) => ReactElement<GridActionsCellItemProps>[];
  /**
   * Flag indicating if valid clients should be shown first
   */
  validFirst?: boolean;
}

/**
 * Renders a list of clients that are already loaded
 */
const PreloadedClientList = <T extends { id: number } = Roleplayer>({
  clients,
  columns,
  onClickDelete,
  onClickSelect,
  selectedIds,
  validFirst = false,
  getActions,
  forbiddenClientIds,
  allowSelectOnlyValid = true,
  ...props
}: PreloadedClientListProps<T>): JSX.Element => {
  // Sort clients so that selected clients are on top
  const clientsWithSelectedOnTop = useMemo(() => {
    return clients
      ? [...clients].sort((a, b) => {
          if (selectedIds?.includes(a.id)) {
            return -1;
          } else if (
            validFirst &&
            (a as unknown as BaseRoleplayer).validationStatusID === ClientValidationStatus.valid &&
            (b as unknown as BaseRoleplayer).validationStatusID !== ClientValidationStatus.valid
          ) {
            return -1;
          }
          return 0;
        })
      : clients;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, validFirst]);

  const resultColumns = useMemo<GridColDef<T>[]>(() => {
    const result = [
      ...(columns || [
        {
          field: 'roleplayerName',
          headerName: 'Name',
          flex: 1,
          minWidth: 150,
        },
        {
          field: 'roleplayerTaxStatusNote',
          headerName: 'Tax status',
          minWidth: 150,
          flex: 1,
          valueGetter: (_, row) =>
            // By default cast row to Roleplayer and show default fieldset
            (row as unknown as Roleplayer).roleplayerTaxStatusNote ||
            (row as unknown as Roleplayer).taxStatusNote,
        },
        {
          field: 'roleplayerIDReg',
          headerName: 'ID number or registration',
          minWidth: 150,
          flex: 1,
        },
        {
          flex: 1,
          minWidth: 150,
          field: 'roleplayerDOBEst',
          headerName: 'Date of birth or registration',
          valueGetter: (_, row) =>
            getFormattedDate((row as unknown as Roleplayer).roleplayerDOBEst),
        },
      ]),
    ];

    result.push({
      field: 'actions',
      type: 'actions',
      getActions: (parameters) =>
        [
          onClickDelete && (
            <DataGridDeleteAction
              key="delete"
              onClick={(): void => onClickDelete(parameters.row)}
            />
          ),
          onClickSelect && (
            <Box className="active">
              {forbiddenClientIds?.includes(parameters.row.id) ? (
                <Typography variant="caption">Already added</Typography>
              ) : (
                <>
                  {(!allowSelectOnlyValid ||
                    (parameters.row as unknown as BaseRoleplayer).validationStatusID ===
                      ClientValidationStatus.valid) && (
                    <DataGridSelectAction
                      active={selectedIds?.includes(parameters.row.id)}
                      onClick={(): void => onClickSelect(parameters.row)}
                      key="select"
                    />
                  )}
                  {allowSelectOnlyValid &&
                    (parameters.row as unknown as BaseRoleplayer).validationStatusID !==
                      ClientValidationStatus.valid && (
                      <ClientStatusBadge client={parameters.row as unknown as BaseRoleplayer} />
                    )}
                </>
              )}
            </Box>
          ),
          ...(getActions?.(parameters) || []),
        ].filter(Boolean) as JSX.Element[],
    });

    return result;
  }, [
    allowSelectOnlyValid,
    columns,
    forbiddenClientIds,
    getActions,
    onClickDelete,
    onClickSelect,
    selectedIds,
  ]);

  return <DataGrid {...props} rows={clientsWithSelectedOnTop} columns={resultColumns} />;
};

export default PreloadedClientList;
